import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(
    () => articles.filter((a) => a.slug !== slug).slice(0, 3),
    [slug]
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${article.title} - 暖暖爱视频`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.nuannuanai.com/article/${slug}`}
        ogType="article"
        ogImage={article.coverImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            author: {
              '@type': 'Organization',
              name: article.author,
            },
            datePublished: article.publishDate,
            publisher: {
              '@type': 'Organization',
              name: '暖暖爱视频',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.nuannuanai.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '文章',
                item: 'https://www.nuannuanai.com/article/nuannuanai-juqing-jiexi',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />

      <Navbar />
      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: '文章', path: '/article/nuannuanai-juqing-jiexi' },
          { label: article.title },
        ]}
      />

      <main>
        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.publishDate}</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </span>
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万次阅读` : `${article.views}次阅读`}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 border border-background-200/70"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            {/* Cover Image */}
            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full object-cover rounded-lg"
              />
            </div>

            {/* Article Content */}
            <div className="prose-custom">
              {article.content.map((section, index) => {
                switch (section.type) {
                  case 'heading':
                    return (
                      <h2
                        key={index}
                        className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4"
                      >
                        {section.content}
                      </h2>
                    );
                  case 'paragraph':
                    return (
                      <p
                        key={index}
                        className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5"
                      >
                        {section.content}
                      </p>
                    );
                  case 'image':
                    return (
                      <div key={index} className="my-8 rounded-lg overflow-hidden">
                        <img
                          src={section.content}
                          alt="文章配图"
                          title="文章配图"
                          className="w-full object-cover rounded-lg"
                        />
                      </div>
                    );
                  case 'quote':
                    return (
                      <blockquote
                        key={index}
                        className="border-l-4 border-primary-400 pl-5 py-3 my-8 bg-primary-50/50 rounded-r-lg"
                      >
                        <p className="text-base md:text-lg text-foreground-700 italic font-heading leading-relaxed">
                          {section.content}
                        </p>
                      </blockquote>
                    );
                  default:
                    return null;
                }
              })}
            </div>

            {/* Author */}
            <div className="mt-12 pt-8 border-t border-background-200/70 flex items-center gap-4">
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                <i className="ri-user-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                <p className="text-xs text-foreground-400">暖暖爱视频编辑团队</p>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                          {ra.category}
                        </span>
                        <span className="text-xs text-foreground-400">{ra.readTime}</span>
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}